import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const { Order, Customer, UI } = Mobi2Go;

export default function StripePaymentElement({
    paymentProcessor, // Inherits from {class.OrderPaymentProcessors}
    layout, // "tabs" or "accordion" (Stripe controlled)
    progressPhase, // For mobile view, elements in the DOM should be hidden if not "unconfirmed"
    isApplePayEnabled // We won't render Apple Pay if false.
}) {
    const [t] = useTranslation('order.confirmation');
    const mountableLinkAuthElem = useRef(null); // DOM
    const mountablePaymentElem = useRef(null); // DOM

    useEffect(() => {
        let stripe = null; // Stripe.js
        let elements = null; // Stripe Elements
        let linkAuthElem = null; // Stripe's Link Authentication Element (mountable)
        let paymentElem = null; // Stripe's Payment Element (mountable)
        let isFormValid = true;

        // This isn't ideal, but needed unfortunately in v3 because of
        // how {class.OrderPaymentProcessors} delegates validation and response
        // handling from the server. When the user in the confirmation window
        // tries to click "PAY NOW", validation is delegated to this object.
        paymentProcessor.__stripe_form = {
            isCreditCardNumberValid: () => isFormValid,
            isCreditCardExpiryValid: () => isFormValid,
            isExtraDetailsValid: () => isFormValid,
            /**
             * After a Payment Intent is created server-side, if any action
             * is needed like SCA, this method gets invoked. We also invoke
             * it during recovery like a page refresh by the user and an
             * action still needing completion.
             *
             * @param clientSecret Payment Intent's client secret
             * @param callback
             */
            handleAction: (clientSecret, callback) => {
                stripe
                    .handleNextAction({ clientSecret })
                    .then(({ error, paymentIntent }) => {
                        if (error) {
                            callback(error);
                            return;
                        }

                        callback(null, paymentIntent);
                    })
                    .catch(error => {
                        callback(error);
                    })
                ;
            },
            /**
             * Because our flow requires finalising payments on the
             * server-side, and we don't have an intent yet, we must create
             * a payment method that will be used to create the intent.
             *
             * Reference:
             * https://stripe.com/docs/payments/finalize-payments-on-the-server?platform=web&type=payment#submit-payment
             *
             */
            createPaymentMethod: async callback => {
                const { error: submitError } = await elements.submit();

                if (submitError) {
                    showInvalidAlert();
                    Order.cancelConfirmation();

                    return;
                }

                stripe
                    .createPaymentMethod({ elements })
                    .then(result => {
                        callback(null, {
                            is_payment_method: true,
                            payment_method: result.paymentMethod,
                        });
                    })
                    .catch(error => {
                        callback(error);
                    })
                ;
            },
        };

        /**
         * If validation fails, our Storefront wants to show an invalid alert.
         * We'll dictate what should be rendered in the alert.
         */
        const showInvalidAlert = () => {
            UI.centeredAlert({
                id: 'invalid-card-details',
                title: t('invalid_cc_details_with_link_title'),
                body: t('invalid_cc_details_with_link_body'),
                temp: true,
            });
        };

        UI.bind('invalidCreditCardDetailsWarning', showInvalidAlert);

        /**
         * Stripe sends us events as the user fills out the form so that
         * we can determine when the form "seems" submittable.
         */
        const handlePaymentElemChange = e => {
            isFormValid = false;

            if (!e.error && e.complete === true) {
                isFormValid = true;

                return;
            }
        };

        /**
         * Render the Payment Element form.
         */
        const renderForm = () => {

            elements = null;

            stripe = window.Stripe(
                paymentProcessor._properties.publishable_key
            );

            const { amount, currency_code } = Order.getPaymentTotal();

            // We can't render if there's no positive amount to pay.
            if (amount <= 0) {
                return;
            }

            elements = stripe.elements({
                mode: 'payment',
                currency: String(currency_code).toLowerCase(),
                amount: Math.round(amount * 100),
                paymentMethodTypes: ['link', 'card'],
                paymentMethodCreation: 'manual',
                appearance: {
                    theme: 'stripe',
                },
            });

            const linkAuthElemOptions = {};

            if (Customer.getEmail()) {
                linkAuthElemOptions.defaultValues = {
                    email: Customer.getEmail(),
                };
            }

            linkAuthElem = elements.create('linkAuthentication', linkAuthElemOptions);

            paymentElem = elements.create('payment', {
                business: {
                    name: Mobi2Go.headoffice.display_name,
                },
                defaultValues: {
                    billingDetails: {
                        // This helps prefill the required fields for Link.
                        name: Customer.getName(),
                        phone: Customer.getPhoneNumber(),
                    },
                },
                layout: layout ?? 'tabs',
                paymentMethodOrder: ['apple_pay', 'google_pay', 'link', 'card'],
                wallets: {
                    applePay: isApplePayEnabled ? 'auto' : 'never',
                    googlePay: 'auto',
                },
            });

            paymentElem.on('change', handlePaymentElemChange);

            linkAuthElem.mount(mountableLinkAuthElem.current);
            paymentElem.mount(mountablePaymentElem.current);

            // If an action is still required from the user (due to causes
            // like the user accidentally refreshing the window), we should be
            // able to pick up where we left off and let Stripe handle it. We
            // currently don't do this for the legacy Card Element - the user
            // is stuck on a loader until the order gets abandoned :(.
            if (
                paymentProcessor.status === 'confirming' &&
                paymentProcessor.requires_action &&
                paymentProcessor.hasOwnProperty('onStripeActionHandled')
            ) {
                paymentProcessor.__stripe_form.handleAction(
                    paymentProcessor.requires_action,
                    paymentProcessor.onStripeActionHandled
                );
            }
        };

        /**
         * Although the Payment Intent (created later by the server) is the
         * source of truth for the payment amount, we need to know when the
         * order's payment total changes because the form may not have been
         * rendered before with a 0 total. And also Apple Pay/Google Pay may
         * display the total figure in the form.
         */
        const updateAmount = ({ amount }) => {

            if (amount <= 0) {
                return;
            }

            // Rendered before...
            if (elements) {
                elements.update({ amount: Math.round(amount * 100) });
            } else {
                renderForm();
            }
        };

        // Event hooks

        Order.bind({
            updatePaymentTotal: updateAmount,
        });

        Customer.bind({
            logIn: renderForm,
        });

        // Load Stripe as an external script to remain PCI compliant, and
        // trigger the rendering of the form.
        if (!window.Stripe) {
            Mobi2Go.helpers.loadScripts(
                ['https://js.stripe.com/v3/'],
                true,
                renderForm
            );
        }
        // Stripe was already loaded before, the component must have been
        // remounted due to an order becoming completed or abandoned.
        else {
            renderForm();
        }

        // Cleanup
        return () => {
            if (paymentElem) {
                paymentElem.unmount();
            }

            if (linkAuthElem) {
                linkAuthElem.unmount();
            }

            Order.unbind({
                updatePaymentTotal: updateAmount,
            });

            Customer.unbind({
                logIn: renderForm,
            });

            UI.unbind(
                'invalidCreditCardDetailsWarning',
                showInvalidAlert
            );
        };
    }, []);

    return (
        <div
            className="m2g-order-confirmation-payment-cc-stripe-form --payment-element"
            style={ progressPhase && {
                display: (progressPhase === 'unconfirmed' ? 'block' : 'none'),
            }}
        >
            <div
                className="m2g-order-confirmation-payment-cc-stripe-form-mount-element"
                data-id="order-confirmation-payment-cc-stripe-form-mount-element"
            >
                <div ref={mountableLinkAuthElem}></div>
                <div ref={mountablePaymentElem}></div>
            </div>
        </div>
    );
}
